import { HttpBackend, HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { LocalStorageToken } from '../../util/constant';
import { LoginResponse, UserModel } from '../models/userModel';
import { IdNameModel } from '../models/IdNameModel';

interface UserWithRoles extends UserModel {
  roles: IdNameModel[];
}

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  baseUrl = environment.apiUrl + '/account';
  private http = inject(HttpClient);
  private directHttp = new HttpClient(inject(HttpBackend));
  router = inject(Router);
  isLoading = false;

  private userSubject = new BehaviorSubject<UserWithRoles | null>(null);
  user$ = this.userSubject.asObservable();

  get user(): UserWithRoles | null {
    return this.userSubject.value;
  }

  setUser(user: UserWithRoles | null) {
    this.userSubject.next(user);
  }

  login(
    username: string | null | undefined,
    password: string | null | undefined,
  ) {
    this.isLoading = true;
    return this.http
      .post<LoginResponse>(`${this.baseUrl}/login`, {
        username,
        password,
      })
      .pipe(
        map((response) => {
          localStorage.setItem(LocalStorageToken, response.token);
          this.getUser();
          this.isLoading = false;
          return response;
        }),
        catchError((error) => {
          this.isLoading = false;
          console.error('Login error:', error);
          throw error;
        }),
      );
  }

  exchangeMicrosoftToken(accessToken: string) {
    return this.directHttp
      .post<LoginResponse>(`${this.baseUrl}/exchange`, { accessToken })
      .pipe(
        map((response) => {
        localStorage.removeItem(LocalStorageToken);
          localStorage.setItem(LocalStorageToken, response.token);
          this.getUser();
          return response;
        }),
      );
  }

  changePassword(
    oldPassword: string | null | undefined,
    newPassword: string | null | undefined,
    newPasswordConfirm: string | null | undefined,
  ) {
    this.isLoading = true;
    return this.http
      .patch(`${this.baseUrl}/change-password`, {
        oldPassword,
        newPassword,
        newPasswordConfirm,
      })
      .pipe(
        map((response) => {
          this.isLoading = false;
          return response;
        }),
        catchError((error) => {
          this.isLoading = false;
          console.error('Change password error:', error);
          throw error;
        }),
      );
  }

  logout() {
    this.isLoading = true;
    return this.http.post(`${this.baseUrl}/logout`, {}).subscribe({
      next: () => {
        localStorage.removeItem(LocalStorageToken);
        this.router.navigate(['/login']);
        this.setUser(null);
      },
      error: (error) => {
        console.error('API error:', error);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  getUser() {
    this.isLoading = true;
    return this.http
      .get<UserModel>(`${this.baseUrl}/get-current-user`)
      .subscribe({
        next: (response) => {
          if (response != null) {
            this.setUser({
              ...response,
              roles: response.roles.concat(
                response.jobTitleRoles,
                response.departmentRoles,
              ),
            });
          }
        },
        error: (error) => {
          console.error('API error:', error);
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }
}
